import { LogOut, Puzzle } from "lucide-mui";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { Link, useNavigate } from "react-router-dom";
import ContextRow from "../../components/ContextRow";
import ThemeModeToggle from "../../components/ThemeModeToggle";
import UserAvatar from "../../components/UserAvatar";
import { useAuth } from "../../contexts/AuthContext";
import { accountNavItems } from "./navItems";
import { flyoutListSx } from "./styles";

const FLYOUT_ID = "account";
const AVATAR_SIZE = 28;

interface AccountMenuProps {
  collapsed: boolean;
  pinnedFlyoutId: string | null;
  setPinnedFlyoutId: (id: string | null) => void;
}

/** The signed-in user: links to their account pages, appearance, developer mode, Hive MCP, log out. */
export default function AccountMenu({ collapsed, pinnedFlyoutId, setPinnedFlyoutId }: AccountMenuProps) {
  const { user, logout, isDeveloper, developerMode, setDeveloperMode } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  const close = () => setPinnedFlyoutId(null);
  const handleLogout = async () => {
    close();
    await logout();
    navigate("/login");
  };

  const flyout = (
    <List disablePadding sx={flyoutListSx}>
      <ListItemButton component={Link} to="/account/profile" onClick={close} sx={{ py: 1, gap: 1.25 }}>
        <UserAvatar
          displayName={user.display_name}
          email={user.email}
          pictureUrl={user.picture_url}
          size={32}
          tooltip={false}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
            {user.display_name}
          </Typography>
          <Typography variant="caption" noWrap component="div" sx={{ color: "text.secondary" }}>
            {user.email}
          </Typography>
        </Box>
      </ListItemButton>
      <Divider />
      {accountNavItems.map((l) => (
        <ListItemButton key={l.path} component={Link} to={l.path} onClick={close}>
          <ListItemIcon>{l.icon}</ListItemIcon>
          <ListItemText primary={l.label} />
        </ListItemButton>
      ))}
      <Divider />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.75, py: 0.75 }}>
        <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
          Theme
        </Typography>
        <ThemeModeToggle compact />
      </Box>
      {isDeveloper && (
        <Box
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1.75,
            py: 0.25,
            cursor: "pointer",
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
            Developer mode
          </Typography>
          <Switch size="small" checked={developerMode} onChange={(_, on) => setDeveloperMode(on)} />
        </Box>
      )}
      <ListItemButton component={Link} to="/connect/mcp" onClick={close}>
        <ListItemIcon>
          <Puzzle />
        </ListItemIcon>
        <ListItemText primary="Connect Hive MCP" />
      </ListItemButton>
      <Divider />
      <ListItemButton onClick={handleLogout}>
        <ListItemIcon>
          <LogOut />
        </ListItemIcon>
        <ListItemText primary="Log out" />
      </ListItemButton>
    </List>
  );

  return (
    <>
      <ContextRow
        label={user.display_name || user.email}
        secondary={user.display_name ? user.email : undefined}
        icon={
          <UserAvatar
            displayName={user.display_name}
            email={user.email}
            pictureUrl={user.picture_url}
            size={AVATAR_SIZE}
            tooltip={false}
          />
        }
        iconSize={AVATAR_SIZE}
        collapsed={collapsed}
        flyoutContent={flyout}
        flyoutId={FLYOUT_ID}
        pinnedFlyoutId={pinnedFlyoutId}
        setPinnedFlyoutId={setPinnedFlyoutId}
        placement="right-end"
      />
    </>
  );
}
